import { useCallback, useEffect, useRef, useState } from 'react';
import { addPage, hasMore, type ListPage, type PagedList } from '@/utils/pagedList';
import { useLatestRequest } from './useLatestRequest';

export interface PagedListState<T, S> {
  list: PagedList<T, S> | null;
  /** The first page is loading. */
  loading: boolean;
  /** The first page failed: show "not loaded", not "nothing flagged". */
  failed: boolean;
  loadingMore: boolean;
  moreFailed: boolean;
  hasMore: boolean;
  loadMore: () => void;
  reload: () => void;
}

/**
 * A server list loaded a page at a time. Loads the first page whenever `fetchPage` changes (give it a new
 * identity when the filter changes) and adds the next one on `loadMore`. `rowKey` must keep its identity.
 */
export function usePagedList<T, S = undefined>(
  fetchPage: (offset: number, limit: number) => Promise<ListPage<T, S>>,
  rowKey: (row: T) => string,
  pageSize = 100
): PagedListState<T, S> {
  const [list, setList] = useState<PagedList<T, S> | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const listRef = useRef(list);
  listRef.current = list;
  const begin = useLatestRequest();

  const reload = useCallback(async () => {
    const isLatest = begin();
    setList(null);
    setLoading(true);
    setFailed(false);
    setLoadingMore(false);
    setMoreFailed(false);
    try {
      const page = await fetchPage(0, pageSize);
      if (isLatest()) setList(addPage(null, page, rowKey));
    } catch (err) {
      console.error('List failed to load:', err);
      if (isLatest()) setFailed(true);
    } finally {
      if (isLatest()) setLoading(false);
    }
  }, [begin, fetchPage, pageSize, rowKey]);

  useEffect(() => {
    reload();
  }, [reload]);

  const loadMore = useCallback(async () => {
    const current = listRef.current;
    // No list while the first page loads, so this never cuts a reload short.
    if (!hasMore(current) || !current) return;
    const isLatest = begin();
    setLoadingMore(true);
    setMoreFailed(false);
    try {
      const page = await fetchPage(current.next, pageSize);
      if (isLatest()) setList((prev) => prev && addPage(prev, page, rowKey));
    } catch (err) {
      console.error('Next page failed to load:', err);
      if (isLatest()) setMoreFailed(true);
    } finally {
      if (isLatest()) setLoadingMore(false);
    }
  }, [begin, fetchPage, pageSize, rowKey]);

  return {
    list,
    loading,
    failed,
    loadingMore,
    moreFailed,
    hasMore: hasMore(list),
    loadMore,
    reload,
  };
}
